// src/pages/logistica/PepsAuditoria.jsx
import { useEffect, useState } from 'react';
import api from '../../services/api';

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function PepsAuditoria() {
  const [articulos, setArticulos] = useState([]);
  const [fecha, setFecha] = useState(hoyISO());
  const [codigoUpc, setCodigoUpc] = useState('');
  const [lotes, setLotes] = useState([]);
  const [loteUsado, setLoteUsado] = useState('');
  const [loteManual, setLoteManual] = useState(''); // si el lote que salió ya no aparece en la lista actual
  const [usarManual, setUsarManual] = useState(false);
  const [ajustarResultado, setAjustarResultado] = useState(false);
  const [comentario, setComentario] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    api.get('/api/logistica/peps/articulos').then((res) => setArticulos(res.data.data)).catch(console.error);
    cargarHistorial();
  }, []);

  async function cargarHistorial() {
    try {
      const res = await api.get('/api/logistica/peps/movimientos');
      setHistorial(res.data.data.slice(0, 20));
    } catch (err) {
      console.error(err);
    }
  }

  async function handleElegirArticulo(upc) {
    setCodigoUpc(upc);
    setLoteUsado('');
    setLoteManual('');
    setUsarManual(false);
    setAjustarResultado(false);
    setComentario('');
    if (!upc) { setLotes([]); return; }
    try {
      const res = await api.get('/api/logistica/peps/lotes-disponibles', { params: { codigo_upc: upc } });
      setLotes(res.data.data);
    } catch (err) {
      console.error(err);
    }
  }

  const articuloSeleccionado = articulos.find((a) => a.codigo_upc === codigoUpc);
  const loteSugerido = lotes.find((l) => l.sugerido);
  const loteUsadoFinal = usarManual ? loteManual.trim() : loteUsado;
  const correctoAutomatico = loteUsadoFinal && loteSugerido ? loteUsadoFinal === loteSugerido.numero_lote : null;
  const correctoFinal = ajustarResultado ? !correctoAutomatico : correctoAutomatico;

  async function handleGuardar() {
    if (!codigoUpc || !loteUsadoFinal) {
      setMensaje({ tipo: 'error', texto: 'Elige el artículo y el lote que realmente salió.' });
      return;
    }
    if (ajustarResultado && !comentario.trim()) {
      setMensaje({ tipo: 'error', texto: 'Si corriges el resultado, explica por qué en el comentario.' });
      return;
    }
    try {
      setGuardando(true);
      setMensaje(null);
      await api.post('/api/logistica/peps/movimiento', {
        fecha_movimiento: fecha,
        codigo_upc: codigoUpc,
        articulo: articuloSeleccionado?.articulo,
        numero_lote_usado: loteUsadoFinal,
        numero_lote_sugerido: loteSugerido?.numero_lote || null,
        correcto: correctoFinal,
        ajustado_manualmente: ajustarResultado,
        comentario: comentario.trim() || null,
      });
      setMensaje({ tipo: 'ok', texto: `✓ Movimiento guardado — ${correctoFinal ? 'Correcto' : 'Incorrecto'}` });
      handleElegirArticulo(''); // limpia el formulario
      cargarHistorial();
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo guardar.' });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div style={styles.contenedor} className="contenedor-captura">
      <h1 style={styles.titulo}>Cumplimiento PEPS — Auditoría</h1>
      <p style={styles.subtitulo}>Registra qué lote salió en un movimiento; el sistema sugiere cuál debía salir según fecha de caducidad.</p>

      {mensaje && <div style={{ ...styles.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48' }}>{mensaje.texto}</div>}

      <div style={styles.tarjeta}>
        <div style={styles.controles}>
          <label style={styles.label}>
            Fecha del movimiento
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={styles.input} />
          </label>
          <label style={{ ...styles.label, flex: 1 }}>
            Artículo
            <select value={codigoUpc} onChange={(e) => handleElegirArticulo(e.target.value)} style={styles.input}>
              <option value="">Elegir artículo…</option>
              {articulos.map((a) => (
                <option key={a.codigo_upc} value={a.codigo_upc}>{a.articulo}</option>
              ))}
            </select>
          </label>
        </div>

        {codigoUpc && (
          <>
            <div style={styles.seccionTitulo}>¿Cuál lote salió realmente?</div>
            <div style={styles.listaLotes}>
              {lotes.map((l) => (
                <label key={l.numero_lote} style={{ ...styles.opcionLote, ...(loteUsado === l.numero_lote && !usarManual ? styles.opcionLoteActiva : {}) }}>
                  <input
                    type="radio"
                    name="lote"
                    checked={!usarManual && loteUsado === l.numero_lote}
                    onChange={() => { setLoteUsado(l.numero_lote); setUsarManual(false); }}
                  />
                  <span>Lote {l.numero_lote} — vence {new Date(l.fecha_caducidad).toLocaleDateString('es-MX')}</span>
                  {l.sugerido && <span style={styles.tagSugerido}>🎯 debía salir primero</span>}
                </label>
              ))}
              <label style={{ ...styles.opcionLote, ...(usarManual ? styles.opcionLoteActiva : {}) }}>
                <input type="radio" name="lote" checked={usarManual} onChange={() => setUsarManual(true)} />
                <span>Otro lote (ya no está en la lista actual):</span>
                <input
                  type="text"
                  placeholder="número de lote"
                  value={loteManual}
                  onChange={(e) => { setLoteManual(e.target.value); setUsarManual(true); }}
                  style={styles.inputLoteManual}
                />
              </label>
            </div>

            {loteUsadoFinal && loteSugerido && (
              <div style={styles.resultadoPreview}>
                <span style={{ color: correctoAutomatico ? '#3E7C5A' : '#B23A48', fontWeight: 700 }}>
                  {correctoAutomatico ? '✓ Correcto — salió el lote sugerido' : '✗ Incorrecto — no salió el lote con fecha más próxima'}
                </span>
                <label style={styles.checkboxAjuste}>
                  <input type="checkbox" checked={ajustarResultado} onChange={(e) => setAjustarResultado(e.target.checked)} />
                  Corregir este resultado (yo sé algo que el sistema no sabe)
                </label>
                {ajustarResultado && (
                  <textarea
                    placeholder="Explica por qué el resultado real es distinto al sugerido…"
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    style={styles.textarea}
                  />
                )}
              </div>
            )}

            <button onClick={handleGuardar} disabled={guardando || !loteUsadoFinal} style={styles.botonGuardar}>
              {guardando ? 'Guardando...' : 'Guardar movimiento'}
            </button>
          </>
        )}
      </div>

      {historial.length > 0 && (
        <div style={styles.tarjeta}>
          <div style={styles.seccionTitulo}>Últimos movimientos auditados</div>
          {historial.map((h, idx) => (
            <div key={idx} style={styles.filaHistorial}>
              <div>
                <div style={styles.historialArticulo}>{h.articulo}</div>
                <div style={styles.historialMeta}>{new Date(h.fecha_movimiento).toLocaleDateString('es-MX')} · lote {h.numero_lote_usado}{h.ajustado_manualmente ? ' · ajustado' : ''}</div>
              </div>
              <span style={{ color: h.correcto ? '#3E7C5A' : '#B23A48', fontWeight: 700, fontSize: '12px' }}>
                {h.correcto ? '✓ Correcto' : '✗ Incorrecto'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  contenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42', maxWidth: '720px' },
  titulo: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '2px' },
  subtitulo: { fontSize: '12px', color: '#8A7690', marginBottom: '18px' },
  mensaje: { fontSize: '13px', marginBottom: '14px', fontWeight: 600 },
  tarjeta: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)', marginBottom: '16px' },
  controles: { display: 'flex', gap: '14px', marginBottom: '16px', flexWrap: 'wrap' },
  label: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: '#8A7690', fontWeight: 600, textTransform: 'uppercase' },
  input: { padding: '8px 10px', fontSize: '13px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7' },
  seccionTitulo: { fontSize: '12px', fontWeight: 700, color: '#2A1830', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.4px' },
  listaLotes: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' },
  opcionLote: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', border: '1px solid #E6DDD2', borderRadius: '8px', fontSize: '12.5px', cursor: 'pointer', flexWrap: 'wrap' },
  opcionLoteActiva: { borderColor: '#6A2C75', background: '#F5F0F7' },
  tagSugerido: { marginLeft: 'auto', fontSize: '10.5px', fontWeight: 700, color: '#6A2C75', background: '#EFE9F3', padding: '2px 8px', borderRadius: '10px' },
  inputLoteManual: { padding: '5px 8px', fontSize: '12px', border: '1px solid #D6A644', borderRadius: '6px', width: '140px', marginLeft: 'auto' },
  resultadoPreview: { background: '#F5F3F0', borderRadius: '8px', padding: '12px', marginBottom: '14px', display: 'flex', flexDirection: 'column', gap: '8px' },
  checkboxAjuste: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#6E4F80', cursor: 'pointer' },
  textarea: { width: '100%', minHeight: '60px', padding: '8px', fontSize: '12px', border: '1px solid #D6A644', borderRadius: '6px', fontFamily: 'Inter, sans-serif', background: '#FFFDF7', resize: 'vertical' },
  botonGuardar: { width: '100%', padding: '10px', fontSize: '13px', fontWeight: 600, color: '#fff', background: '#6A2C75', border: 'none', borderRadius: '8px', cursor: 'pointer' },
  filaHistorial: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F0E9E0' },
  historialArticulo: { fontSize: '12.5px', fontWeight: 600, color: '#2A1830' },
  historialMeta: { fontSize: '10.5px', color: '#8A7690', marginTop: '2px' },
};